import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowRight, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useQuote } from '../context/QuoteContext';

const QuoteCart = () => {
  const { quoteItems, updateQuantity, removeFromQuote, getTotalItems } = useQuote();
  const navigate = useNavigate();

  if (quoteItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <ShoppingCart className="h-24 w-24 mx-auto text-gray-400 mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Quote Cart is Empty</h2>
          <p className="text-gray-600 mb-8">
            Add products to your quote request to get started.
          </p>
          <Link to="/products/all">
            <Button size="lg">
              Browse Products
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-pink-900 to-pink-700 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">Quote Request Cart</h1>
          <p className="text-pink-100">
            {getTotalItems()} item{getTotalItems() !== 1 ? 's' : ''} in your quote request
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {quoteItems.map((item) => (
              <Card key={item.id}>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Product Image */}
                    <Link to={`/product/${item.product_code}`} className="flex-shrink-0">
                      <div
                        className="w-full sm:w-32 h-32 rounded-lg bg-cover bg-center bg-gray-100"
                        style={{ backgroundImage: `url(${item.local_image ? `https://procloudpromo.com${item.local_image}` : item.image_url})` }}
                      />
                    </Link>

                    {/* Product Info */}
                    <div className="flex-1">
                      <Link to={`/product/${item.product_code}`}>
                        <h3 className="text-xl font-semibold mb-1 hover:text-pink-600">
                          {item.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-600 mb-2">Code: {item.product_code}</p>
                      <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                        {item.description}
                      </p>
                      <p className="text-xs text-gray-500">Min Qty: {item.minQty}</p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.product_code, item.quantity - 10)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            updateQuantity(
                              item.product_code,
                              Math.max(1, parseInt(e.target.value) || 1)
                            )
                          }
                          className="w-20 text-center border rounded-md px-2 py-1"
                          min={1}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.product_code, item.quantity + 10)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromQuote(item.product_code)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Link to="/products/all">
              <Button variant="outline" className="w-full">
                Continue Shopping
              </Button>
            </Link>
          </div>

          {/* Quote Summary */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-6">Quote Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Items:</span>
                    <span className="font-semibold">{getTotalItems()} units</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Product Lines:</span>
                    <span className="font-semibold">{quoteItems.length}</span>
                  </div>
                </div>

                <div className="border-t pt-6 mb-6">
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-pink-900">
                      <strong>Custom Pricing</strong><br />
                      Pricing is based on quantity, decoration method, and customization. Request a quote to get your personalized pricing.
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/request-quote')}
                  size="lg"
                  className="w-full mb-3"
                >
                  Request Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>

                <div className="text-center text-sm text-gray-600">
                  Questions? Call us at<br />
                  <strong className="text-pink-600">1300 722 659</strong>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteCart;
